import { useEffect, useState, useRef } from "react";
import { Package, Trash2, PlusCircle, Save, X, Layers } from "lucide-react";
import Swal from "sweetalert2";
import api from "../services/api";

export default function PurchaseSujetoExcluidoForm({ onSaved, onCancel }) {
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);

  const [supplierId, setSupplierId] = useState("");
  const [fechaCompra, setFechaCompra] = useState("");
  const [items, setItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [lineSearch, setLineSearch] = useState({});
  const inputRefs = useRef({});

  useEffect(() => {
    fetchSuppliers();
    fetchProducts();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const res = await api.get("/suppliers");
      setSuppliers(res.data?.data || res.data || []);
    } catch {
      setSuppliers([]);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await api.get("/products");
      setProducts(res.data?.data || res.data || []);
    } catch {
      setProducts([]);
    }
  };

  // Punto de venta ya no se pide aquí: viaja automáticamente en el header
  // X-Punto-Venta (interceptor de api.js) y el middleware SetPuntoVenta lo
  // resuelve en el backend a partir de localStorage("punto_venta_id").

  /* =========================
     ÍTEMS
  ========================== */
  const addItem = () => {
    const newIndex = items.length;
    setItems(prev => [
      ...prev,
      {
        product_id: "",
        presentation_id: null,
        descripcion: "",
        cantidad: 1,
        precio_unitario: 0,
        productData: null,
      },
    ]);
    setTimeout(() => inputRefs.current[newIndex]?.focus(), 50);
  };

  const updateItem = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;

    if (field === "product_id") {
      const selectedProduct = products.find(p => p.id == value);
      updated[index].productData = selectedProduct;
      updated[index].presentation_id = null;
      // Autocompletar descripción con el nombre del producto (editable después)
      if (selectedProduct && !updated[index].descripcion) {
        updated[index].descripcion = selectedProduct.nombre;
      }
    }
    setItems(updated);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
    setLineSearch(prev => {
      const copy = { ...prev };
      delete copy[index];
      return copy;
    });
  };

  /* =========================
     BÚSQUEDA POR LÍNEA
  ========================== */
  const handleLineSearchChange = (index, value) => {
    const q = value.toLowerCase();
    const results = value
      ? products.filter(p =>
          p.codigo?.toLowerCase().includes(q) ||
          p.nombre?.toLowerCase().includes(q)
        ).slice(0, 6)
      : [];
    setLineSearch(prev => ({ ...prev, [index]: { search: value, results } }));
  };

  const handleLineKeyDown = (e, index) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const state = lineSearch[index];
      if (!state?.search) return;
      if (state.results.length > 0) {
        selectProductForLine(index, state.results[0]);
      }
    }
  };

  const selectProductForLine = (index, product) => {
    updateItem(index, "product_id", product.id);
    setLineSearch(prev => ({
      ...prev,
      [index]: { search: product.nombre, results: [] },
    }));
  };

  const tienePresentacionesVariante = (item) => {
    if (!item.productData?.presentations) return false;
    return item.productData.presentations.some(p => p.tipo_presentacion === 'variante');
  };

  /* =========================
     CÁLCULOS — SIN IVA
  ========================== */
  const calcularSubtotalLinea = (item) => {
    return Number(item.precio_unitario || 0) * Number(item.cantidad || 0);
  };

  const totalCompra = items.reduce((sum, it) => sum + calcularSubtotalLinea(it), 0);

  /* =========================
     PROVEEDOR SELECCIONADO — validación visual
  ========================== */
  const supplierSeleccionado = suppliers.find(s => s.id == supplierId);
  const supplierSinDatos = supplierSeleccionado &&
    (!supplierSeleccionado.numero_documento && !supplierSeleccionado.nit);

  /* =========================
     ACTIONS
  ========================== */
  const resetForm = () => {
    setSupplierId("");
    setFechaCompra("");
    setItems([]);
    setLineSearch({});
  };

  const handleCancel = () => {
    resetForm();
    onCancel?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!supplierId || !fechaCompra || items.length === 0) {
      Swal.fire("Error", "Complete todos los campos obligatorios", "error");
      return;
    }

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item.product_id) {
        Swal.fire("Error", `Seleccione un producto en la línea ${i + 1}`, "error");
        return;
      }
      if (!item.descripcion?.trim()) {
        Swal.fire("Error", `Ingrese una descripción en la línea ${i + 1}`, "error");
        return;
      }
      if (tienePresentacionesVariante(item) && !item.presentation_id) {
        Swal.fire(
          "Error",
          `Debe seleccionar una presentación para ${item.productData.nombre}`,
          "error"
        );
        return;
      }
    }

    const payload = {
      supplier_id: supplierId,
      fecha_compra: fechaCompra,
      items: items.map((it) => ({
        product_id: it.product_id,
        presentation_id: it.presentation_id || null,
        descripcion: it.descripcion,
        cantidad: Number(it.cantidad),
        precio_unitario: Number(it.precio_unitario),
      })),
    };

    setSubmitting(true);
    try {
      await api.post("/purchases-sujeto-excluido", payload);
      Swal.fire("Guardado", "Compra a Sujeto Excluido registrada y transmitida a Hacienda", "success");
      resetForm();
      onSaved?.();
    } catch (error) {
      const observaciones = error.response?.data?.observaciones;
      const detalle = Array.isArray(observaciones) ? observaciones.join(", ") : "";
      Swal.fire(
        "Error",
        (error.response?.data?.error || error.response?.data?.message || "Error al guardar la compra") +
          (detalle ? `: ${detalle}` : ""),
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  /* =========================
     UI
  ========================== */
  return (
    <div className="card mb-4">
      <div className="card-header">
        <div className="d-flex align-items-center gap-2">
          <Package size={24} color="#2E8B57" />
          <h2 className="card-title">Nueva Compra — Sujeto Excluido</h2>
        </div>
      </div>

      <div className="card-body">
        <form onSubmit={handleSubmit}>
          {/* DATOS GENERALES */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "16px"
          }} className="mb-4">
            <div className="form-group">
              <label className="form-label">Proveedor (Sujeto Excluido) *</label>
              <select
                className="form-control"
                value={supplierId}
                onChange={(e) => setSupplierId(e.target.value)}
                required
              >
                <option value="">Seleccione un proveedor</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>{s.nombre}</option>
                ))}
              </select>
              {supplierSinDatos && (
                <small style={{ color: "#e53e3e", fontSize: "12px", display: "block", marginTop: "4px" }}>
                  ⚠️ Este proveedor no tiene DUI/documento registrado. Edítelo antes de continuar.
                </small>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Fecha de Compra *</label>
              <input
                type="date"
                className="form-control"
                value={fechaCompra}
                onChange={(e) => setFechaCompra(e.target.value)}
                required
              />
            </div>
          </div>

          <div style={{ borderTop: "2px solid #e2e8f0", margin: "24px 0" }}></div>

          {/* PRODUCTOS */}
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 style={{ fontSize: "16px", fontWeight: 600, margin: 0 }}>Productos</h4>
              <button type="button" className="btn btn-primary btn-sm" onClick={addItem}>
                <PlusCircle size={16} />
                Agregar Producto
              </button>
            </div>

            {items.length === 0 ? (
              <div className="alert alert-info text-center">
                No hay productos agregados. Haz clic en "Agregar Producto" para comenzar.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {items.map((it, index) => {
                  const ls = lineSearch[index] || { search: "", results: [] };

                  return (
                    <div key={index}>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: tienePresentacionesVariante(it)
                            ? "1.5fr 1.5fr 1fr 1fr 1fr 60px"
                            : "1.5fr 1fr 1fr 1fr 60px",
                          gap: "12px",
                          padding: "12px 12px 0 12px",
                          background: "#f7fafc",
                          borderRadius: "8px 8px 0 0",
                          alignItems: "end",
                        }}
                      >
                        {/* PRODUCTO */}
                        <div className="form-group" style={{ marginBottom: 0, position: "relative" }}>
                          <label className="form-label" style={{ fontSize: "12px" }}>Producto *</label>
                          <input
                            ref={el => inputRefs.current[index] = el}
                            type="text"
                            className="form-control"
                            placeholder="Buscar producto..."
                            value={ls.search}
                            onChange={(e) => handleLineSearchChange(index, e.target.value)}
                            onKeyDown={(e) => handleLineKeyDown(e, index)}
                            autoComplete="off"
                            style={{
                              borderColor: it.product_id ? "#2E8B57" : undefined,
                              fontSize: "13px"
                            }}
                          />
                          {ls.results.length > 0 && (
                            <div
                              className="card"
                              style={{
                                position: "absolute", top: "100%", left: 0, right: 0,
                                zIndex: 1000, maxHeight: "200px", overflowY: "auto",
                                marginTop: "2px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                              }}
                            >
                              {ls.results.map(p => (
                                <div
                                  key={p.id}
                                  onMouseDown={() => selectProductForLine(index, p)}
                                  style={{ padding: "8px 14px", cursor: "pointer", borderBottom: "1px solid #e2e8f0", fontSize: "13px" }}
                                  onMouseEnter={e => e.currentTarget.style.background = "#f0fff4"}
                                  onMouseLeave={e => e.currentTarget.style.background = "white"}
                                >
                                  <strong style={{ color: "#2E8B57" }}>{p.codigo}</strong> — <span>{p.nombre}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* PRESENTACIÓN */}
                        {tienePresentacionesVariante(it) && (
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label className="form-label" style={{ fontSize: "12px", display: "flex", alignItems: "center", gap: "4px" }}>
                              <Layers size={12} />
                              Presentación *
                            </label>
                            <select
                              className="form-control"
                              value={it.presentation_id || ""}
                              onChange={(e) => updateItem(index, "presentation_id", e.target.value)}
                              required
                              style={{ borderColor: "#805ad5" }}
                            >
                              <option value="">Seleccionar variante</option>
                              {it.productData.presentations
                                .filter(pres => pres.tipo_presentacion === 'variante')
                                .map((pres) => (
                                  <option key={pres.id} value={pres.id}>{pres.descripcion}</option>
                                ))}
                            </select>
                          </div>
                        )}

                        {/* CANTIDAD */}
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label className="form-label" style={{ fontSize: "12px" }}>Cantidad *</label>
                          <input
                            type="number"
                            className="form-control"
                            min="0.01"
                            step="0.01"
                            value={it.cantidad}
                            onChange={(e) => updateItem(index, "cantidad", e.target.value)}
                            required
                          />
                        </div>

                        {/* PRECIO UNITARIO */}
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label className="form-label" style={{ fontSize: "12px" }}>Precio Unitario *</label>
                          <input
                            type="number"
                            className="form-control"
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                            value={it.precio_unitario}
                            onChange={(e) => updateItem(index, "precio_unitario", e.target.value)}
                            required
                          />
                        </div>

                        {/* SUBTOTAL */}
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label className="form-label" style={{ fontSize: "12px" }}>Subtotal</label>
                          <input
                            type="text"
                            className="form-control"
                            value={`$${calcularSubtotalLinea(it).toFixed(2)}`}
                            disabled
                            style={{ background: "#e2e8f0", fontWeight: 600, color: "#2d3748" }}
                          />
                        </div>

                        {/* ELIMINAR */}
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => removeItem(index)}
                          style={{ height: "46px" }}
                          title="Eliminar producto"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>

                      {/* DESCRIPCIÓN — fila propia, ancho completo */}
                      <div style={{ padding: "0 12px 12px 12px", background: "#f7fafc", borderRadius: "0 0 8px 8px" }}>
                        <label className="form-label" style={{ fontSize: "12px" }}>
                          Descripción para el DTE *
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Descripción tal como aparecerá en el documento"
                          value={it.descripcion}
                          onChange={(e) => updateItem(index, "descripcion", e.target.value)}
                          required
                          style={{ fontSize: "13px" }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div style={{ borderTop: "2px solid #e2e8f0", margin: "24px 0" }}></div>

          {/* TOTAL — SIN IVA */}
          <div style={{ background: "#f0fff4", padding: "20px", borderRadius: "10px" }} className="mb-4">
            <div style={{ display: "flex", flexDirection: "column", gap: "12px", maxWidth: "400px", marginLeft: "auto" }}>
              <div className="d-flex justify-content-between align-items-center">
                <span style={{ fontSize: "18px", fontWeight: 700 }}>Total de la Compra:</span>
                <span style={{ fontSize: "20px", fontWeight: 700, color: "#2E8B57" }}>${totalCompra.toFixed(2)}</span>
              </div>
            </div>
            <small style={{ color: "#718096", display: "block", marginTop: "16px", fontStyle: "italic" }}>
              * No aplica IVA — Sujeto Excluido no está autorizado a cobrarlo
            </small>
          </div>

          {/* BOTONES */}
          <div className="d-flex gap-2">
            <button type="submit" className="btn btn-success" disabled={submitting}>
              <Save size={18} />
              {submitting ? "Transmitiendo a Hacienda..." : "Guardar y Transmitir"}
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleCancel} disabled={submitting}>
              <X size={18} />
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}